import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Check, Close, Colorize, ForumOutlined, LabelOutlined, Reply, Schedule, ViewWeekOutlined } from "@material-ui/icons";
import { ColorBox } from 'material-ui-color';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { Chip, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Typography } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { getCrmColors } from "../../theme/crmColors";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		marginTop: theme.spacing(1),
		minWidth: 120,
	},
	colorAdorment: {
		width: 20,
		height: 20,
		borderRadius: 5,
	},
	dialogPaper: {
		borderRadius: 16,
		margin: 16,
		width: "calc(100% - 32px)",
		maxHeight: "calc(100% - 32px)",
		color: (theme.crm || (theme.mode === "dark" ? getCrmColors(theme, "dark") : getCrmColors(theme, "light"))).text,
		background: (theme.crm || (theme.mode === "dark" ? getCrmColors(theme, "dark") : getCrmColors(theme, "light"))).surface,
		border: `1px solid ${(theme.crm || (theme.mode === "dark" ? getCrmColors(theme, "dark") : getCrmColors(theme, "light"))).border}`,
		"& .MuiIconButton-root:not(.Mui-disabled)": { color: theme.palette.text.primary },
	},
	kanbanTitle: {
		padding: "18px 22px 14px",
		"& h2": { display: "flex", alignItems: "center", gap: 10, fontWeight: 750, fontSize: 21 },
	},
	close: { marginLeft: "auto", marginRight: -8 },
	kanbanForm: { display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" },
	kanbanContent: { padding: "8px 22px 18px", border: 0, minHeight: 0, overflowY: "auto" },
	sectionHeading: {
		display: "flex", alignItems: "center", gap: 8,
		margin: "13px 0 4px", paddingTop: 14,
		borderTop: `1px solid ${(theme.crm || (theme.mode === "dark" ? getCrmColors(theme, "dark") : getCrmColors(theme, "light"))).border}`,
		fontSize: 12, fontWeight: 750, letterSpacing: ".15px",
		color: (theme.crm || (theme.mode === "dark" ? getCrmColors(theme, "dark") : getCrmColors(theme, "light"))).muted,
		"& svg": { fontSize: 17 },
	},
	firstHeading: { marginTop: 0, paddingTop: 0, borderTop: 0 },
	automationStatus: {
		marginLeft: "auto", height: 22, fontSize: 11,
		background: (theme.crm || (theme.mode === "dark" ? getCrmColors(theme, "dark") : getCrmColors(theme, "light"))).raised,
		color: (theme.crm || (theme.mode === "dark" ? getCrmColors(theme, "dark") : getCrmColors(theme, "light"))).muted,
	},
	kanbanActions: {
		padding: "14px 22px", flexShrink: 0,
		borderTop: `1px solid ${(theme.crm || (theme.mode === "dark" ? getCrmColors(theme, "dark") : getCrmColors(theme, "light"))).border}`,
		"& .MuiButton-root": { borderRadius: 8, textTransform: "none", fontWeight: 700, boxShadow: "none" },
	},
	loadState: { padding: "32px 20px", textAlign: "center", "& p": { marginBottom: 12 } },
	colorPicker: { overflowX: "auto", maxWidth: "100%" },

	palette: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 },
	swatch: { width: 32, height: 32, borderRadius: 8, padding: 3, border: "2px solid transparent", backgroundClip: "content-box", cursor: "pointer", display: "grid", placeItems: "center", "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 3 }, "&[aria-pressed=true]": { borderColor: theme.palette.text.primary }, "& svg": { fontSize: 18, color: "#FFFFFF", filter: "drop-shadow(0 1px 2px #000)" } },
	preview: { display: "flex", alignItems: "center", gap: 10, padding: 16, marginTop: 18, borderRadius: 10, border: `1px solid ${(theme.crm || getCrmColors(theme, "light")).border}`, background: (theme.crm || getCrmColors(theme, "light")).inset, overflowWrap: "anywhere", "& svg": { flexShrink: 0 }, "& span": { fontWeight: 650 } },
}));

const TagSchema = Yup.object().shape({
	name: Yup.string()
		.min(3, "Mensagem muito curta")
		.max(100, "Máximo de 100 caracteres")
		.required("Obrigatório"),
	color: Yup.string()
		.matches(/^#[0-9a-f]{6}$/i, "Informe uma cor hexadecimal válida")
		.required("Obrigatório"),
	timeLane: Yup.number()
		.nullable()
		.transform((value, originalValue) => originalValue === "" ? 0 : value)
		.typeError("Informe um número de horas")
		.integer("Informe horas inteiras")
		.min(0, "O tempo não pode ser negativo")
		.max(8760, "O tempo máximo é de 8760 horas")
});

function getRandomHexColor() {
	const red = Math.floor(Math.random() * 256);
	const green = Math.floor(Math.random() * 256);
	const blue = Math.floor(Math.random() * 256);

	return `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
}

const createInitialState = kanban => ({
	name: "",
	color: getRandomHexColor(),
	kanban,
	timeLane: 0,
	nextLaneId: null,
	greetingMessageLane: "",
	rollbackLaneId: null,
});

const TagModal = ({ open, onClose, onSaved, tagId, kanban }) => {
	const classes = useStyles();
	const isKanban = Number(kanban) === 1;
	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [lanes, setLanes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState(false);
	const [loadAttempt, setLoadAttempt] = useState(0);
	const [saving, setSaving] = useState(false);
	const savingRef = useRef(false);
	const [selectedLane, setSelectedLane] = useState(null);
	const [selectedRollbackLane, setSelectedRollbackLane] = useState(null);
	const [tag, setTag] = useState(() => createInitialState(kanban));

	useEffect(() => {
		if (!open) return undefined;

		let active = true;
		setTag(createInitialState(kanban));
		setLanes([]);
		setSelectedLane(null);
		setSelectedRollbackLane(null);
		setColorPickerModalOpen(false);
		setLoading(true);
		setLoadError(false);

		const loadTagModal = async () => {
			try {
				const [lanesResponse, tagResponse] = await Promise.all([
					Number(kanban) === 1
						? api.get("/tags/list", { params: { kanban: 1 } })
						: Promise.resolve({ data: [] }),
					tagId
						? api.get(`/tags/${tagId}`)
						: Promise.resolve({ data: null })
				]);

				if (!active) return;

				setLanes((lanesResponse.data || []).filter(
					lane => Number(lane.id) !== Number(tagId)
				));

				if (tagResponse.data) {
					setTag({ ...createInitialState(kanban), ...tagResponse.data });
					setSelectedLane(tagResponse.data.nextLaneId || null);
					setSelectedRollbackLane(tagResponse.data.rollbackLaneId || null);
				}
			} catch (err) {
				if (active) { setLoadError(true); toastError(err); }
			} finally {
				if (active) setLoading(false);
			}
		};

		loadTagModal();
		return () => {
			active = false;
		};
	}, [open, tagId, kanban, loadAttempt]);

	const handleClose = () => {
		if (savingRef.current) return;
		setTag(createInitialState(kanban));
		setSelectedLane(null);
		setSelectedRollbackLane(null);
		setLanes([]);
		setColorPickerModalOpen(false);
		onClose();
	};

	const handleSaveTag = async values => {
		if (loading || loadError || savingRef.current) return;
		const tagData = { ...values, kanban, nextLaneId: selectedLane || null, rollbackLaneId: selectedRollbackLane || null };
		savingRef.current = true;
		setSaving(true);
		try {
			let response;
			if (tagId) {
				response = await api.put(`/tags/${tagId}`, tagData);
			} else {
				response = await api.post("/tags", tagData);
			}
			toast.success(kanban === 0 ? `${i18n.t("tagModal.success")}` : `${i18n.t("tagModal.successKanban")}`);
			savingRef.current = false;
			if (onSaved) onSaved(response.data);
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			savingRef.current = false;
			setSaving(false);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth={isKanban ? "sm" : "xs"} fullWidth scroll="paper" aria-labelledby="form-dialog-title" classes={{ paper: classes.dialogPaper }}>
				<DialogTitle id="form-dialog-title" className={classes.kanbanTitle}>
					{isKanban ? <ViewWeekOutlined color="primary" /> : <LabelOutlined color="primary" />}
					{isKanban ? (tagId ? "Editar etapa" : "Nova etapa") : (tagId ? (kanban === 0 ? `${i18n.t("tagModal.title.edit")}` : `${i18n.t("tagModal.title.editKanban")}`) :
						(kanban === 0 ? `${i18n.t("tagModal.title.add")}` : `${i18n.t("tagModal.title.addKanban")}`))
					}
					<IconButton size="small" className={classes.close} onClick={handleClose} disabled={saving} aria-label={isKanban ? "Fechar configuração da etapa" : "Fechar tag"}><Close /></IconButton>
				</DialogTitle>
				<Formik
					key={`${open ? "open" : "closed"}-${tagId || "new"}-${kanban}`}
					initialValues={tag}
					enableReinitialize={true}
					validationSchema={TagSchema}
					onSubmit={async (values, actions) => {
						await handleSaveTag(values);
						actions.setSubmitting(false);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form noValidate className={classes.kanbanForm}>
							<DialogContent className={classes.kanbanContent}>
								{loading ? <div className={classes.loadState}><CircularProgress size={28} aria-label="Carregando configuração" /></div> : loadError ? <div className={classes.loadState} role="alert"><Typography>Não foi possível carregar a configuração.</Typography><Button color="primary" onClick={() => setLoadAttempt(current => current + 1)}>Tentar novamente</Button></div> : <Grid container spacing={isKanban ? 2 : 1}>
									{isKanban && <Grid item xs={12}><div className={`${classes.sectionHeading} ${classes.firstHeading}`}>Identificação</div></Grid>}
									<Grid item xs={12} sm={isKanban ? 7 : 12}>
										<Field
											as={TextField}
											label={i18n.t("tagModal.form.name")}
											name="name"
											id="tag-name"
											autoFocus
											disabled={isSubmitting}
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
									</Grid>
									<Grid item xs={12} sm={isKanban ? 5 : 12}>
										<Field
											as={TextField}
											fullWidth
											label={i18n.t("tagModal.form.color")}
											name="color"
											disabled={isSubmitting}
											id="color"
											error={touched.color && Boolean(errors.color)}
											helperText={touched.color && errors.color}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<div style={{ backgroundColor: values.color }} className={classes.colorAdorment}></div>
													</InputAdornment>
												),
												endAdornment: (
													<IconButton size="small" color="default" disabled={isSubmitting} aria-label="Escolher cor" onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}>
														<Colorize />
													</IconButton>
												),
											}}
											variant="outlined"
											margin="dense"
										/>

										{!isKanban && <div className={classes.palette} aria-label="Cores da tag">
											{["#82B1FF", "#4DB6AC", "#81C784", "#FFB74D", "#F06292", "#BA68C8", "#FF8A65", "#90A4AE"].map(color => <button key={color} type="button" className={classes.swatch} style={{ backgroundColor: color }} aria-label={`Usar cor ${color}`} aria-pressed={values.color.toUpperCase() === color} disabled={isSubmitting} onClick={() => setFieldValue("color", color)}>{values.color.toUpperCase() === color && <Check />}</button>)}
										</div>}
										{colorPickerModalOpen && !isSubmitting && (
											<div className={classes.colorPicker}>
												<ColorBox
													disableAlpha={true}
													hslGradient={false}
													style={{ margin: '20px auto 0' }}
													value={values.color}
													onChange={val => setFieldValue("color", `#${val.hex}`)}
												/>
											</div>
										)}
									</Grid>

									{!isKanban && <Grid item xs={12}><div className={classes.preview} aria-label="Prévia da tag"><LabelOutlined style={{ color: values.color }} /><span>{values.name.trim() || "Sua tag"}</span></div></Grid>}

									{isKanban && (
										<>
											<Grid item xs={12}><div className={classes.sectionHeading}><Schedule />Avanço automático<Chip size="small" className={classes.automationStatus} label={selectedLane && Number(values.timeLane) > 0 ? "Ativo" : "Desativado"} /></div></Grid>
											<Grid item xs={12} sm={4}>
												<Field
													as={TextField}
													label="Após (horas)"
													name="timeLane"
													id="tag-time-lane"
													type="number"
													inputProps={{ min: 0, max: 8760, step: 1 }}
													error={touched.timeLane && Boolean(errors.timeLane)}
													helperText={(touched.timeLane && errors.timeLane) || "0 = desativado"}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
											</Grid>
											<Grid item xs={12} sm={8}>
												<FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
													<InputLabel id="next-lane-label">Avançar para</InputLabel>
													<Field
														as={Select}
														label="Avançar para"
														labelId="next-lane-label"
														id="nextLaneId"
														name="nextLaneId"
														error={touched.nextLaneId && Boolean(errors.nextLaneId)}
														value={selectedLane || ""}
														onChange={(e) => setSelectedLane(e.target.value || null)}
													>
														<MenuItem value="">Sem avanço automático</MenuItem>
														{lanes && lanes.map((lane) => (
															<MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>
														))}
													</Field>
												</FormControl>
											</Grid>
											<Grid item xs={12}><div className={classes.sectionHeading}><ForumOutlined />Entrada por automação</div></Grid>
											<Grid item xs={12}>
												<Field
													as={TextField}
													label="Mensagem de entrada"
													name="greetingMessageLane"
													id="tag-greeting-message"
													rows={3}
													multiline
													error={touched.greetingMessageLane && Boolean(errors.greetingMessageLane)}
													helperText={touched.greetingMessageLane && errors.greetingMessageLane}
													variant="outlined"
													margin="dense"
													fullWidth
												/>
											</Grid>
											<Grid item xs={12}><div className={classes.sectionHeading}><Reply />Resposta do cliente</div></Grid>
											<Grid item xs={12}>
												<FormControl variant="outlined" margin="dense" fullWidth className={classes.formControl}>
													<InputLabel id="rollback-lane-label">Retornar para</InputLabel>
													<Field
														as={Select}
														label="Retornar para"
														labelId="rollback-lane-label"
														id="rollbackLaneId"
														name="rollbackLaneId"
														error={touched.rollbackLaneId && Boolean(errors.rollbackLaneId)}
														value={selectedRollbackLane || ""}
														onChange={(e) => setSelectedRollbackLane(e.target.value || null)}
													>
														<MenuItem value="">Manter a etapa atual</MenuItem>
														{lanes && lanes.map((lane) => (
															<MenuItem key={lane.id} value={lane.id}>{lane.name}</MenuItem>
														))}
													</Field>
												</FormControl>
											</Grid>
										</>
									)}
								</Grid>}

							</DialogContent>
							<DialogActions className={classes.kanbanActions}>
								<Button onClick={handleClose} color="default" disabled={isSubmitting} variant="outlined">
									{i18n.t("tagModal.buttons.cancel")}
								</Button>
								<Button type="submit" color="primary" disabled={isSubmitting || loading || loadError} variant="contained" className={classes.btnWrapper}>
									{isKanban ? "Salvar etapa" : "Salvar tag"}
									{isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default TagModal;
